import LoadingContent from "@/components/LoadingContent";
import useToast from "@/contexts/Toast/useToast";
import useGetRecordingAudio from "@/hooks/queries/useGetRecordingAudio";
import { useRecordingList } from "@/hooks/useRecordingList";
import { Recording } from "@/models/recording";
import {
  ActionMenu,
  ButtonSize,
  Color,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  IconButton,
  LoaderSize,
  Modal,
  ModalSize,
  MoreVerticalIcon,
  PopoverSize,
  Space,
  UploadIcon,
  VerticalFlex,
} from "@enxcs/dap-ui-stratos";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface RecordingTableRowActions {
  record: Recording;
  onUpload?(): void;
  handleEdit?(): void;
}

export function RecordingTableRowActions({ record, onUpload, handleEdit }: RecordingTableRowActions) {
  const { t } = useTranslation();
  const { recordingId, exports = [] } = record;
  const { onDeleteRow } = useRecordingList();
  const { showErrorToast } = useToast();

  const downloadViaAnchor = (url: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const downloadViaBlob = async (url: string, fileName: string) => {
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Audio download failed with status ${response.status}`);
    }

    const audioBlob = await response.blob();
    const blobUrl = URL.createObjectURL(audioBlob);

    downloadViaAnchor(blobUrl, fileName);

    // Delay revocation to avoid canceling the download in some browsers.
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  };

  const [isDownloadingAudio, setIsDownloadingAudio] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const { refetchRecordingAudio } = useGetRecordingAudio(recordingId || "", false);
  const triggerAudioDownload = async () => {
    if (!recordingId || isDownloadingAudio) {
      return;
    }

    setIsDownloadingAudio(true);
    try {
      const refetchResult = await refetchRecordingAudio();
      const audioUrl = typeof refetchResult.data === "string" && refetchResult.data ? refetchResult.data : undefined;

      if (!audioUrl) {
        showErrorToast(t("failMessage.toast"));
        return;
      }
      const fileName = `${recordingId}.wav`;

      await downloadViaBlob(audioUrl, fileName).catch(() => {
        downloadViaAnchor(audioUrl, fileName);
      });
    } catch {
      showErrorToast(t("failMessage.toast"));
    } finally {
      setIsDownloadingAudio(false);
    }
  };

  const options = [
    ...(exports.length
      ? [
          {
            label: t("buttons.edit"),
            icon: EditIcon,
            iconTone: Color.Teal.V40,
            onClick: () => handleEdit?.(),
          },
          {
            label: t("buttons.export"),
            icon: UploadIcon,
            iconTone: Color.Teal.V40,
            onClick: () => onUpload?.(),
          },
        ]
      : []),
    {
      label: t("buttons.delete"),
      icon: DeleteIcon,
      iconTone: Color.Teal.V40,
      onClick: handleOpenModal,
    },
    {
      label: t("buttons.download"),
      icon: DownloadIcon,
      iconTone: Color.Teal.V40,
      onClick: triggerAudioDownload,
    },
  ];

  function handleOpenModal() {
    const selecteds = new Map();
    selecteds.set(recordingId, { row: recordingId, exports: new Set(exports.map((e) => e.id)) });
    onDeleteRow(selecteds);
  }

  return (
    <>
      {isDownloadingAudio ? (
        <Modal
          body={
            <VerticalFlex $grow $justify="center" $align="center" $gap={Space.V24}>
              <LoadingContent loaderSize={LoaderSize.V32} loaderHeight={LoaderSize.V56} loaderWidth={LoaderSize.V56} />
              Downloading audio...
            </VerticalFlex>
          }
          closeTitle={t("buttons.close")}
          title=""
          size={ModalSize.S}
        />
      ) : null}
      <IconButton
        size={ButtonSize.S}
        icon={MoreVerticalIcon}
        title="Menu"
        elementRef={menuTriggerRef}
        ariaExpanded={isMenuOpen}
        ariaControls={`recording-row-action-menu-${recordingId}`}
        ariaHaspopup="menu"
        onClick={() => setIsMenuOpen((prev) => !prev)}
      />
      {isMenuOpen && (
        <ActionMenu
          options={options}
          size={PopoverSize.XS}
          triggerRef={menuTriggerRef}
          id={`recording-row-action-menu-${recordingId}`}
          onClose={() => setIsMenuOpen(false)}
        />
      )}
    </>
  );
}
